import AuthPageGuard from "components/AuthPageGuard";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";

interface PasswordStrength {
  score: number; // 0-4 (weak, fair, good, strong, very strong)
  label: string;
  color: string;
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

const calculatePasswordStrength = (password: string): PasswordStrength => {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    // eslint-disable-next-line no-useless-escape
    hasSpecial: /[!@#$%^&*()_+={};\[\]':"\\|,.<>/?]/.test(password),
  };

  let score = 0;
  if (requirements.minLength) score++;
  if (requirements.hasUppercase) score++;
  if (requirements.hasLowercase) score++;
  if (requirements.hasNumber) score++;
  if (requirements.hasSpecial) score++;

  let labelKey = "weak";
  let color = "red";

  if (score >= 5) {
    labelKey = "verystrong";
    color = "green";
  } else if (score >= 4) {
    labelKey = "strong";
    color = "green";
  } else if (score >= 3) {
    labelKey = "good";
    color = "yellow";
  } else if (score >= 2) {
    labelKey = "fair";
    color = "orange";
  }

  return { score, label: labelKey, color, requirements };
};

export default function SignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] =
    useState<PasswordStrength | null>(null);
  const [pendingEmail, setPendingEmail] = useState("");

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value) {
      setPasswordStrength(calculatePasswordStrength(value));
    } else {
      setPasswordStrength(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const email =
      (document.getElementById("email") as HTMLInputElement)?.value || "";
    const companyName =
      (document.getElementById("companyName") as HTMLInputElement)?.value || "";
    const website =
      (document.getElementById("website") as HTMLInputElement)?.value || "";

    if (password !== confirmPassword) {
      setError(t("app.auth.signUp.passwordsDoNotMatch"));
      return;
    }

    if (!passwordStrength || passwordStrength.score < 3) {
      setError(t("app.auth.signUp.passwordTooWeak"));
      return;
    }

    setLoading(true);
    try {
      const result = await authService.register({
        email,
        password,
        companyName: companyName || undefined,
        website: website || undefined,
      });

      if (result.emailVerificationRequired) {
        setPendingEmail(email.trim());
        return;
      }

      navigate("/admin");
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.auth.signUp.registrationFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  if (pendingEmail) {
    return (
      <AuthPageGuard>
        <Box w="full">
          <VStack gap={6} align="stretch">
            <Heading
              size="2xl"
              color={getThemeColor("text.primary")}
              fontWeight="bold"
            >
              {t("app.auth.signUp.checkEmailTitle")}
            </Heading>
            <Card.Root>
              <Card.Body p={8}>
                <VStack gap={3} align="stretch">
                  <Text fontSize="md" color={getThemeColor("text.primary")}>
                    {t("app.auth.signUp.checkEmailBody", {
                      email: pendingEmail,
                    })}
                  </Text>
                  <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                    {t("app.auth.signUp.checkEmailHint")}
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>
            <Flex justify="center">
              <Link to="/auth/sign-in">
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={getThemeColor("text.link")}
                >
                  {t("app.auth.signUp.signInLink")}
                </Text>
              </Link>
            </Flex>
          </VStack>
        </Box>
      </AuthPageGuard>
    );
  }

  return (
    <AuthPageGuard>
      <Box w="full">
        <VStack gap={8} align="stretch">
          {/* Header */}
          <VStack gap={2} align="start">
            <Heading
              size="2xl"
              color={getThemeColor("text.primary")}
              fontWeight="bold"
            >
              {t("app.auth.signUp.title")}
            </Heading>
            <Text fontSize="md" color={getThemeColor("text.secondary")}>
              {t("app.auth.signUp.subtitle")}
            </Text>
          </VStack>

          {/* Error Message */}
          {error && (
            <Box
              bg={getThemeColor("alert.error.bg")}
              border="1px solid"
              borderColor={getThemeColor("alert.error.border")}
              borderRadius="lg"
              p={3}
            >
              <Text
                fontSize="sm"
                color={getThemeColor("alert.error.text")}
                fontWeight="medium"
              >
                {error}
              </Text>
            </Box>
          )}

          {/* Form Card */}
          <Card.Root>
            <Card.Body p={8}>
              <form onSubmit={handleSubmit}>
                <VStack gap={5} align="stretch">
                  {/* Email Field */}
                  <Input
                    id="email"
                    type="email"
                    label={t("app.auth.signUp.emailLabel")}
                    placeholder={t("app.auth.signUp.emailPlaceholder")}
                    size="lg"
                    required
                  />

                  {/* Company Name Field */}
                  <Input
                    id="companyName"
                    type="text"
                    label={t("app.auth.signUp.companyNameLabel")}
                    placeholder={t("app.auth.signUp.companyNamePlaceholder")}
                    size="lg"
                  />

                  {/* Company Website Field (optional — used to generate domain-aware guardrails) */}
                  <Input
                    id="website"
                    type="url"
                    label={t("app.auth.signUp.websiteLabel")}
                    placeholder={t("app.auth.signUp.websitePlaceholder")}
                    size="lg"
                  />

                  {/* Password Field */}
                  <Box>
                    <Input
                      id="password"
                      type="password"
                      label={t("app.auth.signUp.passwordLabel")}
                      placeholder={t("app.auth.signUp.passwordPlaceholder")}
                      size="lg"
                      value={password}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                      required
                    />

                    {/* Password Strength Indicator */}
                    {passwordStrength && (
                      <Box mt={2}>
                        <HStack gap={1} mb={2}>
                          {[1, 2, 3, 4, 5].map((level) => (
                            <Box
                              key={level}
                              flex={1}
                              h="4px"
                              bg={
                                level <= passwordStrength.score
                                  ? passwordStrength.color === "red"
                                    ? "red.500"
                                    : passwordStrength.color === "orange"
                                      ? "orange.500"
                                      : passwordStrength.color === "yellow"
                                        ? "yellow.500"
                                        : "green.500"
                                  : getThemeColor("border.default").base
                              }
                              borderRadius="full"
                            />
                          ))}
                        </HStack>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          color={
                            passwordStrength.color === "red"
                              ? "red.500"
                              : passwordStrength.color === "orange"
                                ? "orange.500"
                                : passwordStrength.color === "yellow"
                                  ? "yellow.600"
                                  : "green.600"
                          }
                        >
                          {t(
                            `app.auth.signUp.passwordStrength.${passwordStrength.label.toLowerCase().replace(" ", "")}`,
                          )}
                        </Text>
                      </Box>
                    )}
                  </Box>

                  {/* Confirm Password Field */}
                  <Input
                    id="confirmPassword"
                    type="password"
                    label={t("app.auth.signUp.confirmPasswordLabel")}
                    placeholder={t(
                      "app.auth.signUp.confirmPasswordPlaceholder",
                    )}
                    size="lg"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    variant="solid"
                    colorPalette="blue"
                    size="lg"
                    w="full"
                    mt={2}
                  >
                    {loading
                      ? t("app.auth.signUp.creatingAccount")
                      : t("app.auth.signUp.signUpButton")}
                  </Button>
                </VStack>
              </form>
            </Card.Body>
          </Card.Root>

          {/* Sign In Link */}
          <Flex justify="center" align="center" gap={1}>
            <Text fontSize="sm" color={getThemeColor("text.secondary")}>
              {t("app.auth.signUp.haveAccount")}
            </Text>
            <Link to="/auth/sign-in">
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={getThemeColor("text.link")}
                _hover={{
                  color: getThemeColor("text.linkHover").base,
                  textDecoration: "underline",
                }}
              >
                {t("app.auth.signUp.signInLink")}
              </Text>
            </Link>
          </Flex>
        </VStack>
      </Box>
    </AuthPageGuard>
  );
}
